"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { createBreakpoint } from "react-use";
import { ApiDrop } from "../../generated/models/ApiDrop";
import { DropSize, ExtendedDrop } from "../../helpers/waves/drop.helpers";
import { useSidebarState } from "../../hooks/useSidebarState";
import { commonApiFetch } from "../../services/api/common-api";
import BrainDesktopDrop from "../brain/BrainDesktopDrop";
import WebBrainLeftSidebar from "../brain/left-sidebar/web/WebLeftSidebar";
import { useLayout } from "../brain/my-stream/layout/LayoutContext";
import BrainRightSidebar, { SidebarTab } from "../brain/right-sidebar/BrainRightSidebar";
import { QueryKey } from "../react-query-wrapper/ReactQueryWrapper";
import { useAuth } from "../auth/Auth";
import useCreateModalState from "@/hooks/useCreateModalState";
import CreateWaveModal from "../waves/create-wave/CreateWaveModal";

// Breakpoint for mobile responsiveness (lg = 1024px)
const useBreakpoint = createBreakpoint({
  XL: 1400,
  LG: 1024,
  S: 0
});
interface WavesMessagesWrapperProps {
  readonly children: ReactNode;
  readonly defaultPath?: string; // "/waves" or "/messages"
  readonly showLeftSidebar?: boolean;
}
const WavesMessagesWrapper: React.FC<WavesMessagesWrapperProps> = t0 => {
  const $ = _c(59);
  const {
    children,
    defaultPath: t1,
    showLeftSidebar: t2
  } = t0;
  const defaultPath = t1 === undefined ? "/waves" : t1;
  const showLeftSidebar = t2 === undefined ? true : t2;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const {
    contentContainerStyle
  } = useLayout();
  const {
    isRightSidebarOpen,
    closeRightSidebar
  } = useSidebarState();
  const {
    connectedProfile
  } = useAuth();
  const {
    isWaveModalOpen,
    close
  } = useCreateModalState();
  const [sidebarTab, setSidebarTab] = useState(SidebarTab.ABOUT);
  let t3;
  let waveId;
  if ($[0] !== searchParams) {
    const rawDropId = searchParams?.get("drop") ?? undefined;
    waveId = searchParams?.get("wave") ?? undefined;
    t3 = rawDropId && /^[a-zA-Z0-9-_]+$/.test(rawDropId) ? rawDropId : undefined;
    $[0] = searchParams;
    $[1] = t3;
    $[2] = waveId;
  } else {
    t3 = $[1];
    waveId = $[2];
  }
  const dropId = t3;
  const isMobile = breakpoint === "S";
  const isLargeDesktop = breakpoint === "XL";
  let t4;
  let t5;
  if ($[3] !== closeRightSidebar || $[4] !== isRightSidebarOpen || $[5] !== waveId) {
    t4 = () => {
      if (!waveId && isRightSidebarOpen) {
        closeRightSidebar();
      }
    };
    t5 = [waveId, isRightSidebarOpen, closeRightSidebar];
    $[3] = closeRightSidebar;
    $[4] = isRightSidebarOpen;
    $[5] = waveId;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== dropId) {
    t6 = [QueryKey.DROP, {
      drop_id: dropId
    }];
    t7 = async () => await commonApiFetch({
      endpoint: `drops/${dropId}`
    });
    $[8] = dropId;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  const t8 = !!dropId;
  let t9;
  if ($[11] !== t6 || $[12] !== t7 || $[13] !== t8) {
    t9 = {
      queryKey: t6,
      queryFn: t7,
      placeholderData: keepPreviousData,
      enabled: t8
    };
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const {
    data: drop,
    error: dropError
  } = useQuery(t9);
  let t10;
  if ($[15] !== defaultPath || $[16] !== pathname || $[17] !== router || $[18] !== searchParams) {
    t10 = () => {
      const params = new URLSearchParams(searchParams?.toString() || "");
      params.delete("drop");
      const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname || defaultPath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[15] = defaultPath;
    $[16] = pathname;
    $[17] = router;
    $[18] = searchParams;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const onDropClose = t10;
  let t11;
  if ($[20] !== pathname || $[21] !== router || $[22] !== searchParams) {
    t11 = drop_0 => {
      const params_0 = new URLSearchParams(searchParams?.toString() || "");
      params_0.set("drop", drop_0.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[20] = pathname;
    $[21] = router;
    $[22] = searchParams;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const onDropClick = t11;
  drop?.id;
  let t12;
  if ($[24] !== drop?.id || $[25] !== dropId) {
    t12 = dropId && drop?.id?.toLowerCase() === dropId?.toLowerCase();
    $[24] = drop?.id;
    $[25] = dropId;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const isDropOpen = Boolean(t12);
  const shouldShowLeftSidebar = showLeftSidebar && (!isMobile || !waveId);
  const shouldShowMainContent = !isMobile || waveId;
  const shouldShowDropOverlay = isDropOpen && drop && shouldShowMainContent;
  const shouldShowRightSidebar = Boolean(isRightSidebarOpen && waveId && !isDropOpen);
  const canInlineRight = !isMobile && (isLargeDesktop || breakpoint === "LG");
  let rightVariant = null;
  if (shouldShowRightSidebar) {
    rightVariant = canInlineRight ? "inline" : "overlay";
  }
  if (dropError && dropId) {
    console.error("Failed to load drop:", dropError);
  }
  let t13;
  if ($[27] !== rightVariant || $[28] !== shouldShowLeftSidebar) {
    t13 = shouldShowLeftSidebar && <WebBrainLeftSidebar isCollapsed={rightVariant === "inline"} />;
    $[27] = rightVariant;
    $[28] = shouldShowLeftSidebar;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== children || $[31] !== drop || $[32] !== onDropClose || $[33] !== shouldShowDropOverlay || $[34] !== shouldShowMainContent) {
    t14 = shouldShowMainContent && <div className="tw-flex-grow tw-flex tw-flex-col tw-h-full tw-min-w-0 tw-border-solid tw-border-r tw-border-iron-800 tw-border-y-0 tw-border-l-0">{children}{shouldShowDropOverlay && <div className="tw-absolute tw-inset-0 tw-z-[49]"><BrainDesktopDrop drop={{
          type: DropSize.FULL,
          ...drop,
          stableKey: drop.id,
          stableHash: drop.id
        }} onClose={onDropClose} /></div>}</div>;
    $[30] = children;
    $[31] = drop;
    $[32] = onDropClose;
    $[33] = shouldShowDropOverlay;
    $[34] = shouldShowMainContent;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== onDropClick || $[37] !== rightVariant || $[38] !== sidebarTab || $[39] !== waveId) {
    t15 = rightVariant === "inline" && <div className="tw-hidden lg:tw-block tw-flex-shrink-0 tw-pl-6 tw-pt-2"><BrainRightSidebar variant="inline" waveId={waveId} onDropClick={onDropClick} activeTab={sidebarTab} setActiveTab={setSidebarTab} /></div>;
    $[36] = onDropClick;
    $[37] = rightVariant;
    $[38] = sidebarTab;
    $[39] = waveId;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== contentContainerStyle || $[42] !== t13 || $[43] !== t14 || $[44] !== t15) {
    t16 = <div className="tw-relative tw-flex tw-flex-col"><div className="tw-relative tw-flex tw-flex-grow"><div className="tw-relative tw-flex tw-flex-grow tw-w-full tw-max-w-full tw-mx-auto"><div className="tw-flex tw-w-full tw-overflow-hidden" style={contentContainerStyle}>{t13}{t14}{t15}</div></div></div></div>;
    $[41] = contentContainerStyle;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== onDropClick || $[47] !== rightVariant || $[48] !== sidebarTab || $[49] !== waveId) {
    t17 = rightVariant === "overlay" && <BrainRightSidebar variant="overlay" waveId={waveId} onDropClick={onDropClick} activeTab={sidebarTab} setActiveTab={setSidebarTab} />;
    $[46] = onDropClick;
    $[47] = rightVariant;
    $[48] = sidebarTab;
    $[49] = waveId;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== close || $[52] !== connectedProfile || $[53] !== isWaveModalOpen) {
    t18 = connectedProfile && <CreateWaveModal isOpen={isWaveModalOpen} onClose={close} profile={connectedProfile} />;
    $[51] = close;
    $[52] = connectedProfile;
    $[53] = isWaveModalOpen;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t16 || $[56] !== t17 || $[57] !== t18) {
    t19 = <>{t16}{t17}{t18}</>;
    $[55] = t16;
    $[56] = t17;
    $[57] = t18;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  return t19;
};
export default WavesMessagesWrapper;